import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Save,
  Loader2,
  Cpu,
  Hash,
  Factory,
  Tag,
  MapPin,
  Calendar,
} from "lucide-react";
import { machinesAPI, plantsAPI } from "../api";
import toast from "react-hot-toast";

const getTodayString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const initialFormState = {
  plantId: "",
  name: "",
  model: "",
  manufacturer: "",
  serialNumber: "",
  locationInPlant: "",
  installationDate: getTodayString(),
};

const AddMachine = ({ isOpen = true, onClose, machineToEdit = null, defaultPlantId = "" }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(initialFormState);
  const { data: plantsResponse, isLoading: arePlantsLoading } = useQuery({
    queryKey: ["plants"],
    queryFn: () => plantsAPI.getAll().then((res) => res.data),
    enabled: isOpen,
  });
  const plants = Array.isArray(plantsResponse?.data) ? plantsResponse.data : [];

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate("/machines");
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (machineToEdit) {
        setFormData({
          plantId: machineToEdit.plant_id || "",
          name: machineToEdit.name || "",
          model: machineToEdit.model || "",
          manufacturer: machineToEdit.manufacturer || "",
          serialNumber: machineToEdit.serial_number || "",
          locationInPlant: machineToEdit.location_in_plant || "",
          installationDate: machineToEdit.installation_date
            ? machineToEdit.installation_date.split("T")[0]
            : getTodayString(),
        });
      } else {
        setFormData({
          ...initialFormState,
          plantId: defaultPlantId || "",
        });
      }
    }
  }, [isOpen, machineToEdit, defaultPlantId]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const mutation = useMutation({
    mutationFn: (data) =>
      machineToEdit
        ? machinesAPI.update(machineToEdit.id, data)
        : machinesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["machines"] });
      if (machineToEdit) {
        queryClient.invalidateQueries({ queryKey: ["machine", machineToEdit.id] });
        toast.success("Machine updated successfully!");
      } else {
        toast.success("Machine unit registered successfully!");
      }
      handleClose();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error ||
        (machineToEdit ? "Failed to update machine" : "Failed to add machine unit")
      );
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Equipment name is required");
      return;
    }
    if (!formData.plantId) {
      toast.error("Plant is required");
      return;
    }
    if (!formData.serialNumber.trim()) {
      toast.error("Serial number is required");
      return;
    }
    const todayStr = getTodayString();
    if (!machineToEdit && formData.installationDate && formData.installationDate < todayStr) {
      toast.error("Installation date cannot be before today");
      return;
    }
    mutation.mutate(formData);
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="relative w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col my-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-100 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100/80 flex items-center justify-center flex-shrink-0">
                <Cpu className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                  {machineToEdit ? "Edit Machine Details" : "Add New Equipment"}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {machineToEdit
                    ? "Update equipment specifications and plant placement"
                    : "Register industrial machine to live telemetry and monitoring"}
                </p>
              </div>
            </div>
          </div>

          {/* Form Body */}
          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="p-5 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto max-h-[calc(85vh-130px)]">
              {/* Row 1: Name & Serial Number */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Plant <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Factory className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <select
                    required
                    value={formData.plantId}
                    disabled={arePlantsLoading}
                    onChange={(e) => setFormData({ ...formData, plantId: e.target.value })}
                    className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                  >
                    <option value="">
                      {arePlantsLoading ? "Loading plants..." : "Select a plant"}
                    </option>
                    {plants.map((plant) => (
                      <option key={plant.id} value={plant.id}>
                        {plant.name}{plant.location ? ` - ${plant.location}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Name & Serial Number */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Machine Name <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Cpu className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="e.g. CNC Milling Center 02"
                      className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Model Number
                  </label>
                  <div className="relative">
                    <Tag className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      value={formData.model}
                      onChange={(e) =>
                        setFormData({ ...formData, model: e.target.value })
                      }
                      placeholder="e.g. DMU 50 3rd Gen"
                      className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Row 3: Manufacturer & Model Number */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Serial Number <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Hash className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      required
                      value={formData.serialNumber}
                      onChange={(e) =>
                        setFormData({ ...formData, serialNumber: e.target.value })
                      }
                      placeholder="e.g. SN-49204-CX"
                      className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Manufacturer / OEM
                  </label>
                  <div className="relative">
                    <Factory className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      value={formData.manufacturer}
                      onChange={(e) =>
                        setFormData({ ...formData, manufacturer: e.target.value })
                      }
                      placeholder="e.g. DMG MORI"
                      className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Row 4: Plant Location & Installation Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Plant Location
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      value={formData.locationInPlant}
                      onChange={(e) =>
                        setFormData({ ...formData, locationInPlant: e.target.value })
                      }
                      placeholder="e.g. Bay 3, Production Line B"
                      className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Installation Date
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="date"
                      min={machineToEdit ? undefined : getTodayString()}
                      value={formData.installationDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        const todayStr = getTodayString();
                        if (!machineToEdit && val && val < todayStr) {
                          toast.error("Installation date cannot be before today");
                          return;
                        }
                        setFormData({ ...formData, installationDate: val });
                      }}
                      className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 sm:px-4 hover:border-slate-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 whitespace-nowrap"
              >
                {mutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                    {machineToEdit ? "Saving..." : "Adding..."}
                  </>
                ) : (
                  <>
                    {machineToEdit ? (
                      <Save className="w-4 h-4 mr-1.5" />
                    ) : (
                      <Plus className="w-4 h-4 mr-1.5" />
                    )}
                    {machineToEdit ? "Save Changes" : "Add Machine"}
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default AddMachine;